import React, { useState, useEffect, useMemo, useCallback } from 'react';

const PRIMARY = '#0052CC';

export default function ODBsPage() {
  const [odbs, setOdbs] = useState([]);
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterZone, setFilterZone] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editOdb, setEditOdb] = useState(null);
  const [form, setForm] = useState({ name: '', zone_id: '', port_count: 16, location: '', fiber_map_object_id: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [portsModal, setPortsModal] = useState(null);
  const [ports, setPorts] = useState([]);

  const api = useMemo(() => {
    const token = localStorage.getItem('session_token');
    const headers = { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
    return {
      get: (url) => fetch(url, { headers }).then(r => r.json()),
      post: (url, body) => fetch(url, { method: 'POST', headers, body: JSON.stringify(body) }).then(r => r.json()),
      put: (url, body) => fetch(url, { method: 'PUT', headers, body: JSON.stringify(body) }).then(r => r.json()),
      del: (url) => fetch(url, { method: 'DELETE', headers }).then(r => r.json()),
    };
  }, []);

  const loadOdbs = useCallback(async () => {
    setLoading(true);
    try {
      let params = [];
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      if (filterZone) params.push(`zone_id=${filterZone}`);
      const data = await api.get(`/api/noc/odbs${params.length ? '?' + params.join('&') : ''}`);
      setOdbs(data.items || []);
    } catch (e) { setError('Failed to load ODBs'); }
    finally { setLoading(false); }
  }, [api, search, filterZone]);

  const loadZones = useCallback(async () => {
    try {
      const data = await api.get('/api/noc/zones');
      setZones(data.items || []);
    } catch (e) {}
  }, [api]);

  useEffect(() => { loadOdbs(); }, [loadOdbs]);
  useEffect(() => { loadZones(); }, [loadZones]);

  const openAdd = () => { setEditOdb(null); setForm({ name: '', zone_id: '', port_count: 16, location: '', fiber_map_object_id: '' }); setShowForm(true); };
  const openEdit = (o) => { setEditOdb(o); setForm({ name: o.name, zone_id: o.zone_id || '', port_count: o.port_count || 16, location: o.location || '', fiber_map_object_id: o.fiber_map_object_id || '' }); setShowForm(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('ODB name is required'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, zone_id: form.zone_id ? parseInt(form.zone_id) : null, port_count: parseInt(form.port_count) || 16 };
      if (editOdb) { await api.put(`/api/noc/odbs/${editOdb.id}`, payload); }
      else { await api.post('/api/noc/odbs', payload); }
      setShowForm(false); loadOdbs();
    } catch (e) { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this ODB and all its ports?')) return;
    try { await api.del(`/api/noc/odbs/${id}`); loadOdbs(); }
    catch (e) { setError('Delete failed'); }
  };

  const viewPorts = async (odb) => {
    setPortsModal(odb);
    try {
      const data = await api.get(`/api/noc/odbs/${odb.id}/ports`);
      setPorts(data.items || []);
    } catch (e) { setPorts([]); }
  };

  return (
    <div style={{ padding: '24px', background: '#f5f7fa', minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#172B4D' }}>ODBs</h2>
          <p style={{ margin: '4px 0 0', color: '#6B778C', fontSize: 13 }}>Optical Distribution Boxes — manage fiber port assignments</p>
        </div>
        <button onClick={openAdd} style={{ background: PRIMARY, color: '#fff', border: 'none', borderRadius: 6, padding: '9px 18px', fontWeight: 600, cursor: 'pointer', fontSize: 14 }}>
          + Add ODB
        </button>
      </div>

      {error && <div style={{ background: '#FFEBE6', border: '1px solid #FF5630', borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#BF2600', fontSize: 13 }}>{error}</div>}

      {/* Filters */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #DFE1E6', padding: '14px 16px', marginBottom: 16, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search ODBs..." onKeyDown={e => e.key === 'Enter' && loadOdbs()}
          style={{ flex: 1, minWidth: 160, border: '1px solid #DFE1E6', borderRadius: 5, padding: '7px 12px', fontSize: 14, outline: 'none' }} />
        <select value={filterZone} onChange={e => setFilterZone(e.target.value)}
          style={{ border: '1px solid #DFE1E6', borderRadius: 5, padding: '7px 12px', fontSize: 14, outline: 'none', minWidth: 160 }}>
          <option value="">All Zones</option>
          {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
        </select>
        <button onClick={loadOdbs} style={{ background: PRIMARY, color: '#fff', border: 'none', borderRadius: 5, padding: '7px 16px', cursor: 'pointer', fontSize: 14 }}>Search</button>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #DFE1E6', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#F4F5F7' }}>
              {['#', 'ODB Name', 'Zone', 'Total Ports', 'Free', 'Used', 'Location', 'Actions'].map(h => (
                <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontWeight: 600, color: '#5E6C84', borderBottom: '1px solid #DFE1E6', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#6B778C' }}>Loading...</td></tr>
            ) : odbs.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 32, textAlign: 'center', color: '#6B778C' }}>No ODBs found. Click "+ Add ODB" to create one.</td></tr>
            ) : odbs.map((o, i) => (
              <tr key={o.id} style={{ borderBottom: '1px solid #F4F5F7' }} onMouseEnter={e => e.currentTarget.style.background='#F8F9FA'} onMouseLeave={e => e.currentTarget.style.background='#fff'}>
                <td style={{ padding: '10px 14px', color: '#6B778C' }}>{i + 1}</td>
                <td style={{ padding: '10px 14px', fontWeight: 600, color: '#172B4D' }}>{o.name}</td>
                <td style={{ padding: '10px 14px' }}>
                  {o.zone_name ? <span style={{ background: '#DEEBFF', color: '#0747A6', borderRadius: 4, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>{o.zone_name}</span> : <span style={{ color: '#6B778C' }}>—</span>}
                </td>
                <td style={{ padding: '10px 14px', color: '#344563' }}>{o.port_count}</td>
                <td style={{ padding: '10px 14px' }}><span style={{ color: '#006644', fontWeight: 600 }}>{o.free_ports || 0}</span></td>
                <td style={{ padding: '10px 14px' }}><span style={{ color: '#BF2600', fontWeight: 600 }}>{o.used_ports || 0}</span></td>
                <td style={{ padding: '10px 14px', color: '#6B778C', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.location || '—'}</td>
                <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                  <button onClick={() => viewPorts(o)} style={{ background: '#DEEBFF', color: '#0052CC', border: 'none', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', marginRight: 5, fontSize: 12 }}>Ports</button>
                  <button onClick={() => openEdit(o)} style={{ background: '#E3FCEF', color: '#006644', border: 'none', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', marginRight: 5, fontSize: 12 }}>Edit</button>
                  <button onClick={() => handleDelete(o.id)} style={{ background: '#FFEBE6', color: '#BF2600', border: 'none', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', fontSize: 12 }}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 500, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ margin: '0 0 20px', fontSize: 18, fontWeight: 700, color: '#172B4D' }}>{editOdb ? 'Edit ODB' : 'Add ODB'}</h3>
            {error && <div style={{ background: '#FFEBE6', border: '1px solid #FF5630', borderRadius: 5, padding: '8px 12px', marginBottom: 14, color: '#BF2600', fontSize: 13 }}>{error}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>ODB Name *</label>
                <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. ODB-Downtown-01"
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Zone</label>
                <select value={form.zone_id} onChange={e => setForm(p => ({ ...p, zone_id: e.target.value }))}
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none' }}>
                  <option value="">Select Zone</option>
                  {zones.map(z => <option key={z.id} value={z.id}>{z.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Port Count</label>
                <input type="number" value={form.port_count} onChange={e => setForm(p => ({ ...p, port_count: e.target.value }))} min={1} max={128}
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Location</label>
                <input value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Physical location description"
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={{ display: 'block', fontWeight: 600, color: '#344563', fontSize: 13, marginBottom: 5 }}>Fiber Map Object ID</label>
                <input value={form.fiber_map_object_id} onChange={e => setForm(p => ({ ...p, fiber_map_object_id: e.target.value }))} placeholder="Optional fiber map reference"
                  style={{ width: '100%', border: '1px solid #DFE1E6', borderRadius: 5, padding: '8px 12px', fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setShowForm(false)} style={{ background: '#F4F5F7', color: '#344563', border: 'none', borderRadius: 5, padding: '9px 20px', cursor: 'pointer', fontWeight: 600 }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ background: PRIMARY, color: '#fff', border: 'none', borderRadius: 5, padding: '9px 20px', cursor: 'pointer', fontWeight: 600 }}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Ports Modal */}
      {portsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 28, width: 560, maxWidth: '95vw', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#172B4D' }}>{portsModal.name} — Ports</h3>
              <button onClick={() => setPortsModal(null)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#6B778C' }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
              {ports.map(p => (
                <div key={p.id} style={{ border: `2px solid ${p.status === 'used' ? '#FF5630' : '#36B37E'}`, borderRadius: 6, padding: '8px 10px', textAlign: 'center', background: p.status === 'used' ? '#FFEBE6' : '#E3FCEF' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: p.status === 'used' ? '#BF2600' : '#006644' }}>Port {p.port_number}</div>
                  <div style={{ fontSize: 11, color: '#6B778C', marginTop: 2 }}>{p.status === 'used' ? (p.onu_serial || 'Used') : 'Free'}</div>
                </div>
              ))}
              {ports.length === 0 && <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#6B778C', padding: 20 }}>No ports found</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
